import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  Outlet,
} from "react-router-dom";
import BaseLayout from "../layouts/base";
import { CommonModule } from "@/modules/common";
import * as AdminPanel from "@/modules/admin-panel";
import { dynamicAttributeRoutes } from "@/modules/dynamic-attributes";
import { profiles } from "@/modules/profile";
import { Suspense } from "@/shared/suspense";
import { QueryParamProvider } from "../providers/query-params-provider";
import { requestsRoutes } from "@/modules/requests/pages";
import { surveys } from "@/modules/survey";
import { absencePoliticsRoutes } from "@/modules/absence-politics";
import { Page404 } from "@/shared/page";
import { reports } from "@/modules/reports";
import { tasksRoutes } from "@/modules/tasks";
import { dictionariesRoutes } from "@/modules/common/pages/dictionaries";
import { signingRoutes } from "@/modules/signing";
import { historicalImportDataRoutes } from "@/modules/historical-data-import";
import { ErrorBoundary } from "@/shared/error-boundary";
import { RouteErrorFallback } from "./route-error-fallback";
import { assetsAccountingRoutes } from "@/modules/assets";
import { companyDocumentsRoutes } from "@/modules/documents";
import { recruitingRoutes } from "@/modules/recruiting";
import { gatewayRoutes } from "@/modules/gateway";
import { calendarRoutes } from "@/modules/calendar";
import { rolesRoutes } from "@/modules/roles";
import { crmRoutes } from "@/modules/crm";
import { SIGN_IN_PATH } from "@/modules/auth/pages/sign-in";
import { fileStorage } from "@/modules/file-storage";
import { jmixReportsRoutes } from "@/modules/jmix-reports";
import { documentRegister } from "@/modules/document-register";
import { myTeamRoutes } from "@/modules/my-team";
import { modulesSettings } from "@/modules/global-wizard";
import { aiRoutes } from "@/modules/ai";
import { settingsMenuRoutes } from "@/modules/settings/routes";
import { timesheetRoutes } from "@/modules/timesheet/routes";
import { feedRoutes } from "@/modules/feed";
import { systemCheckRoutes } from "@/modules/system-check";
import { UserProtectionTokensProvider } from "@/shared/security";
import { UserSettingsProvider } from "@/application/providers/user-settings";
import { SetupModuleSettings } from "@/modules/global-wizard/features/setup-module-settings";
import { ToursProvider } from "@/shared/tours";
import { NotificationProviders } from "@/application/providers/notification-providers";
import { UserContextProvider } from "@/application/providers/user/model/user-context";
import { SectionComponent } from "@/modules/auth/shared/section";
import { authRoutes } from "@/modules/auth/routes";
import { LoginRedirect } from "@/modules/auth/shared/login-redirect";
import { PwaBanner } from "@/shared/pwa/banner";

export const moduleRoutes = [
  {
    element: (
      <SectionComponent section>
        <Suspense>
          <Outlet />
        </Suspense>
      </SectionComponent>
    ),
    children: [...authRoutes, ...gatewayRoutes],
  },

  {
    element: (
      <UserContextProvider>
        <LoginRedirect>
          <NotificationProviders>
            <UserProtectionTokensProvider>
              <UserSettingsProvider>
                <SetupModuleSettings>
                  <ToursProvider>
                    <Outlet />
                    <PwaBanner />
                  </ToursProvider>
                </SetupModuleSettings>
              </UserSettingsProvider>
            </UserProtectionTokensProvider>
          </NotificationProviders>
        </LoginRedirect>
      </UserContextProvider>
    ),
    children: [
      ...crmRoutes,
      ...calendarRoutes,
      ...recruitingRoutes,
      ...companyDocumentsRoutes,
      ...assetsAccountingRoutes,
      ...historicalImportDataRoutes,
      ...signingRoutes,
      ...tasksRoutes,
      ...dictionariesRoutes,
      ...CommonModule.routes,
      ...AdminPanel.routes,
      ...dynamicAttributeRoutes,
      ...profiles,
      ...requestsRoutes,
      ...surveys,
      ...absencePoliticsRoutes,
      ...reports,
      ...rolesRoutes,
      ...fileStorage,
      ...jmixReportsRoutes,
      ...documentRegister,
      ...myTeamRoutes,
      ...modulesSettings,
      ...aiRoutes,
      ...settingsMenuRoutes,
      ...timesheetRoutes,
      ...feedRoutes,
      ...systemCheckRoutes,
      {
        path: SIGN_IN_PATH,
        element: <Navigate to="/" />,
      },
      {
        path: "*",
        element: <Page404 />,
      },
    ].map((route) => {
      route.element = (
        <QueryParamProvider>
          <BaseLayout>
            <ErrorBoundary
              key={route.path} // make error boundary more specific for page and don't save error between pages
              fallback={RouteErrorFallback}
            >
              <Suspense>{route.element}</Suspense>
            </ErrorBoundary>
          </BaseLayout>
        </QueryParamProvider>
      );
      return route;
    }),
  },
];

export const router = createBrowserRouter(moduleRoutes, {
  basename: process.env.PROJECT_ROOT,
});

export default function Routes() {
  return <RouterProvider router={router} />;
}
